import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '../../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[1100] bg-black/40 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// 現在マウント中の DialogContent 数。複数 Dialog が重なる場面で、最後の1つが
// 閉じたときだけ下記クリーンアップを行うためのモジュールスコープカウンタ。
let openDialogContentCount = 0;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  // Dialog オープン直前にフォーカスされていた要素を記録し、閉じる際に
  // それが DOM に残っていれば自前で focus() を戻す。これにより
  // onCloseAutoFocus を preventDefault しても a11y（キーボード/SR）が維持される。
  const previouslyFocusedRef = React.useRef<Element | null>(null);

  // Radix Dialog の既知バグ (radix-ui/primitives#1241) で、Dialog を閉じた後に
  // body の pointer-events: none が残り画面操作不能になることがある。
  // DialogContent がアンマウントされる際にセーフティとして強制的にクリアする。
  React.useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    openDialogContentCount += 1;

    return () => {
      openDialogContentCount -= 1;
      // 他の Dialog がまだ開いている場合、pointer-events 復元もフォーカス
      // 戻しもその Dialog の入力を奪ってしまうためスキップする。
      if (openDialogContentCount > 0) return;

      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
      // a11y: 元のフォーカス要素が DOM に残っていれば手動で戻す。
      // requestAnimationFrame で Radix の cleanup と競合しない次フレームに遅延する。
      const previous = previouslyFocusedRef.current;
      if (previous instanceof HTMLElement && document.contains(previous)) {
        requestAnimationFrame(() => {
          previous.focus({ preventScroll: true });
        });
      }
    };
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay />
      <div className="fixed inset-0 z-[1100] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPrimitive.Content
            ref={ref}
            aria-describedby={undefined}
            // フォーカス戻し先が DOM 更新で消えていたとき body にフォーカスが逃げ、
            // 上記 #1241 を誘発するため、デフォルトのフォーカス戻しを抑止する。
            // ホスト側で onCloseAutoFocus を渡している場合はその後に委譲。
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              onCloseAutoFocus?.(event);
            }}
            className={cn(
              'relative bg-white shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
              className
            )}
            {...props}
          >
            {children}
          </DialogPrimitive.Content>
        </div>
      </div>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 border-b border-solid border-gray-200 px-lg pt-lg pb-4 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse border-t border-solid border-gray-200 px-lg pt-4 pb-lg sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-body-sm text-gray-600', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
